<script setup lang="ts">
import {computed, onMounted, provide, reactive, watch, watchEffect} from 'vue'
import type {DataRow} from '@hantera/design-system'
import {InspectorLayout, type InspectorProvider} from '@hantera/design-system'
import {useContext, useDialogs, useLocalization, useProcesses} from '@hantera/portal-app'
import useBackend from '@/composition/useBackend'
import {usePrincipalStore} from '@/stores/iam/principal-store'
import PrincipalSidebar from './PrincipalSidebar.vue'
import PrincipalPermissionsTab from './PrincipalPermissionsTab.vue'
import PrincipalRolesTab from './PrincipalRolesTab.vue'
import PrincipalSessionsTab from './PrincipalSessionsTab.vue'

const t = useLocalization('iam.principals')

const props = defineProps<{
  row: DataRow
}>()

// Extract principal ID from row
const principalId = computed(() => props.row.key)

// Initialize services in component context
const context = useContext()
const dialogs = useDialogs()
const processes = useProcesses()
const iamService = useBackend().iam

// Create Pinia store for this principal with services
const store = usePrincipalStore(principalId.value, {
  context,
  dialogs,
  processes,
  iamService,
})

// Provide store for child components
provide('principalStore', store)

// Count only revokable sessions (not current, not already revoked)
const revokableSessions = computed(() => {
  return store.sessions.filter((s) => !s.isCurrent && !s.isRevoked)
})

// Create reactive inspector provider
const inspectorProvider: InspectorProvider = reactive({
  state: { label: t('status.loading') },
  title: '',
  labels: [],
  summary: PrincipalSidebar,
  summaryLabel: t('tabs.profile'),
  selectedTab: 'roles',
  tabs: [],
})

// Update inspector state dynamically
watchEffect(() => {
  const p = store.principal

  if (!p) return

  inspectorProvider.state = p.suspendedAt
    ? { label: t('status.suspended'), hue: 0 }
    : { label: t('status.active'), hue: 133 }

  inspectorProvider.title = p.name || p.email

  const rolesCount = p.roles.length
  inspectorProvider.labels = [
    p.email,
  ]
})

// Update tabs dynamically
watchEffect(() => {
  const p = store.principal

  if (!p) {
    inspectorProvider.tabs = []
    return
  }

  const rolesCount = p.roles.length
  const sessionsCount = store.sessions.length

  inspectorProvider.tabs = [
    {
      id: 'roles',
      label: t('tabs.roles'),
      tags: rolesCount > 0 ? [{ style: 'primary', value: rolesCount.toString() }] : [],
      component: PrincipalRolesTab,
    },
    {
      id: 'sessions',
      label: t('tabs.sessions'),
      tags: sessionsCount > 0 ? [{ style: 'primary', value: sessionsCount.toString() }] : [],
      component: PrincipalSessionsTab,
      toolbar: [{
        type: 'button',
        align: 'right',
        buttonStyle: 'danger',
        label: t('actions.revokeCount', { count: revokableSessions.value.length }),
        id: 'revokeAll',
        disabled: revokableSessions.value.length === 0,
        async onClick() {
          if (revokableSessions.value.length === 0) return

          const count = revokableSessions.value.length
          const plural = count !== 1 ? 's' : ''
          const confirmed = await store.dialogs.confirm(
            t('dialogs.revokeSessionsPrompt', { count, plural }),
            t('dialogs.revokeSessionsTitle')
          )

          if (!confirmed) return

          try {
            const principalId = store.principal?.id
            if (!principalId) return

            await Promise.all(
              revokableSessions.value.map((session) => store.iamService.revokePrincipalSession(principalId, session.id))
            )
            await store.refresh()
            store.processes.createProcess({
              description: t('actions.revokeAll'),
            }).complete()
          } catch (error: any) {
            console.error('Failed to revoke sessions:', error)
            store.processes.error({
              message: error.response?.data?.message || 'Failed to revoke sessions',
            })
          }
        }
      }]
    },
    {
      id: 'permissions',
      label: t('tabs.permissions'),
      component: PrincipalPermissionsTab,
    },
  ]
})

// Load data on mount
onMounted(() => {
  store.refresh()
})

// Watch principal ID changes (if row changes)
watch(principalId, (newId) => {
  if (newId) {
    store.refresh()
  }
})



</script>

<template>
  <InspectorLayout v-if="inspectorProvider" :provider="inspectorProvider" />
</template>

<style scoped></style>
